import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const {
  FiClipboard,
  FiUsers,
  FiExternalLink,
  FiRefreshCw,
  FiBriefcase,
} = FiIcons;

function StatCard({ title, value, subtitle, icon }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-gray-500">{title}</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{value}</div>
          {subtitle ? <div className="mt-2 text-sm text-gray-500">{subtitle}</div> : null}
        </div>
        <div className="rounded-xl bg-indigo-50 p-3">
          <SafeIcon icon={icon} className="text-indigo-600" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { session } = useAuth();
  const { tenantId, isSuperadmin } = useTenant();

  const [groups, setGroups] = useState([]);
  const [participantsTotals, setParticipantsTotals] = useState({});
  const [registrationsTotals, setRegistrationsTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      if (isSuperadmin || !tenantId) {
        setGroups([]);
        setParticipantsTotals({});
        setRegistrationsTotals({});
        return;
      }

      const intakeGroups = await spformsApi.intakeGroupsList(tenantId);
      setGroups(intakeGroups);

      const registrationMap = {};
      const participantMap = {};

      await Promise.all(
        intakeGroups.map(async (group) => {
          try {
            const [registrations, participants] = await Promise.all([
              spformsApi.registrationsList(tenantId, group.id),
              spformsApi.participantsList(tenantId, group.id),
            ]);
            registrationMap[group.id] = (registrations || []).length;
            participantMap[group.id] = (participants || []).length;
          } catch (e) {
            console.error(`Error loading data for group ${group.id}`, e);
            registrationMap[group.id] = 0;
            participantMap[group.id] = 0;
          }
        })
      );

      setRegistrationsTotals(registrationMap);
      setParticipantsTotals(participantMap);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [tenantId, isSuperadmin]);

  const totals = useMemo(() => {
    const totalGroups = groups.length;
    const totalRegistrations = Object.values(registrationsTotals).reduce((a, b) => a + b, 0);
    const totalParticipants = Object.values(participantsTotals).reduce((a, b) => a + b, 0);
    const activeGroups = groups.filter((g) => g.status === 'active').length;

    return {
      totalGroups,
      totalRegistrations,
      totalParticipants,
      activeGroups,
    };
  }, [groups, registrationsTotals, participantsTotals]);

  if (isSuperadmin) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Superadmin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage tenants, tenant users, and SaaS onboarding.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Link
            to="/tenants"
            className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50/30"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-indigo-50 p-3">
                <SafeIcon icon={FiBriefcase} className="text-indigo-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Manage Tenants</div>
                <div className="text-sm text-gray-500">
                  Create and update client organizations
                </div>
              </div>
            </div>
          </Link>

          <Link
            to="/tenant-users"
            className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50/30"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-indigo-50 p-3">
                <SafeIcon icon={FiUsers} className="text-indigo-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Manage Tenant Users</div>
                <div className="text-sm text-gray-500">
                  Provision and review tenant accounts
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome, {session?.name || session?.email || 'back'}.
          </p>
        </div>

        <button
          onClick={loadDashboard}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <SafeIcon icon={FiRefreshCw} />
          Refresh
        </button>
      </div>

      {errorMessage ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Intake Groups"
          value={loading ? '...' : totals.totalGroups}
          subtitle="All groups"
          icon={FiClipboard}
        />
        <StatCard
          title="Active Groups"
          value={loading ? '...' : totals.activeGroups}
          subtitle="Currently active"
          icon={FiExternalLink}
        />
        <StatCard
          title="Registrations"
          value={loading ? '...' : totals.totalRegistrations}
          subtitle="Submitted forms"
          icon={FiUsers}
        />
        <StatCard
          title="Participants"
          value={loading ? '...' : totals.totalParticipants}
          subtitle="Approved records"
          icon={FiUsers}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
          <div className="mt-4 grid grid-cols-1 gap-3">
            <Link
              to="/intake-groups"
              className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Go to Intake Groups
            </Link>
            <Link
              to="/participants"
              className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              Review Participants & Export CSV
            </Link>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Recent Intake Groups</h2>

          <div className="mt-4 space-y-3">
            {loading ? (
              <div className="text-sm text-gray-500">Loading groups...</div>
            ) : groups.length === 0 ? (
              <div className="text-sm text-gray-500">No intake groups yet.</div>
            ) : (
              groups.slice(0, 5).map((group) => (
                <div
                  key={group.id}
                  className="rounded-xl border border-gray-200 px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium text-gray-900">{group.title}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {group.type || '-'} • {group.location || 'No location'}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {participantsTotals[group.id] || 0} participants
                      </div>
                      <div className="text-xs text-gray-500">
                        {registrationsTotals[group.id] || 0} registrations
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}